import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Paperclip, Edit, Trash2, Eye, X, ExternalLink, FileText, Download, Clock, CalendarDays, TrendingUp, ChevronDown, ChevronUp, User, LayoutList, CalendarPlus, Award } from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { SiFigma } from 'react-icons/si';
import { useLocation } from 'react-router-dom';
import CreateDailyAssignmentModal from './CreateDailyAssignmentModal';
import UserPerformanceModal from './UserPerformanceModal';
import { getRoleDayPrefix } from '../../../helper/role-helper';

const STATUS_COLORS = {
  Pending: 'bg-yellow-100 text-yellow-700',
  'In Progress': 'bg-blue-100 text-blue-700',
  Submitted: 'bg-purple-100 text-purple-700',
  'Under Review': 'bg-amber-100 text-amber-800 border border-amber-300',
  'Changes Requested': 'bg-orange-100 text-orange-800 border border-orange-300',
  Completed: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Overdue: 'bg-rose-100 text-rose-800 border border-rose-300 font-bold',
};

const PRIORITY_COLORS = {
  High: 'bg-red-100 text-red-600',
  Medium: 'bg-yellow-100 text-yellow-600',
  Low: 'bg-blue-100 text-blue-600',
};

const getDaysRunning = (task) => {
  const from = task.startDate || task.createdAt;
  if (!from) return null;
  return Math.floor((new Date() - new Date(from)) / (1000 * 60 * 60 * 24));
};

const getDaysLeft = (deadline) => {
  if (!deadline) return null;
  return Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
};

// Groups TaskUpdate records by task._id, returns an object { taskId: { taskInfo, updates[] } }
const groupUpdatesByTask = (updates) => {
  const map = {};
  updates.forEach(up => {
    const taskId = up.task?._id || 'unknown';
    if (!map[taskId]) {
      map[taskId] = {
        task: up.task,
        user: up.user,
        updates: [],
      };
    }
    map[taskId].updates.push(up);
  });
  return Object.values(map);
};

// ─── Task Updates Group Card ──────────────────────────────────────────────────
const TaskUpdateGroup = ({ group, getFileUrl, getFileName, getFileType }) => {
  const [expanded, setExpanded] = useState(false);
  const latestUpdate = group.updates[0]; // already sorted desc

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
      {/* Group Header */}
      <div
        className="p-5 cursor-pointer flex justify-between items-start gap-4"
        onClick={() => setExpanded(prev => !prev)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-gray-900 text-base">{group.task?.title || 'Deleted Task'}</span>
            <span className="bg-[#f06600]/10 text-[#f06600] text-xs font-bold px-2 py-0.5 rounded-full">
              {group.updates.length} update{group.updates.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              <strong className="text-gray-700">{group.user?.fullName || 'Unknown'}</strong>
              <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{group.user?.role}</span>
            </span>
            {group.user?.fieldOrDepartment && (
              <span className="text-gray-400">({group.user.fieldOrDepartment})</span>
            )}
          </div>
          {/* Latest update preview */}
          <p className="text-xs text-gray-500 mt-2 line-clamp-1">
            <span className="font-medium text-gray-600">Latest:</span> {latestUpdate?.updateNotes}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className="text-xs text-gray-400">
            {new Date(latestUpdate?.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />
          }
        </div>
      </div>

      {/* Expanded: Day-by-Day Timeline */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4 space-y-4 bg-gray-50">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
            <CalendarDays className="w-3.5 h-3.5" /> Day-by-Day Timeline
          </p>
          {group.updates.map((up, i) => (
            <div key={up._id} className="flex gap-3">
              {/* Timeline connector */}
              <div className="flex flex-col items-center w-5 shrink-0">
                <div className={`w-3 h-3 rounded-full mt-0.5 ${up.updateType === 'Final' ? 'bg-purple-500 ring-2 ring-purple-200' : 'bg-blue-400'}`} />
                {i < group.updates.length - 1 && <div className="w-0.5 bg-gray-200 flex-1 mt-1 min-h-[20px]" />}
              </div>

              {/* Content */}
              <div className="flex-1 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
                  <div>
                    <span className="text-xs font-bold text-gray-500">
                      {new Date(up.createdAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="text-[10px] text-gray-400 ml-2">
                      {new Date(up.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${
                    up.updateType === 'Final'
                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}>
                    {up.updateType === 'Final' ? '🏁 Final Submission' : '📋 Daily Progress'}
                  </span>
                </div>

                {/* Notes */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-100">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{up.updateNotes}</p>
                </div>

                {/* Links */}
                {(up.githubLink || up.liveLink) && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {up.githubLink && (
                      <a href={up.githubLink} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1 bg-gray-900 hover:bg-gray-700 text-white rounded-lg text-xs font-semibold transition-colors">
                        <FaGithub className="w-3 h-3" /> GitHub
                      </a>
                    )}
                    {up.liveLink && (
                      <a href={up.liveLink} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1 bg-[#f06600] hover:bg-[#d05800] text-white rounded-lg text-xs font-semibold transition-colors">
                        <ExternalLink className="w-3 h-3" /> Live Demo
                      </a>
                    )}
                  </div>
                )}

                {/* Files */}
                {up.submittedFiles?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                      Files ({up.submittedFiles.length})
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {up.submittedFiles.map((file, idx) => {
                        const fileUrl = getFileUrl(file);
                        const fileName = getFileName(file);
                        const isImg = getFileType(file) === 'image';
                        return (
                          <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                            {isImg && (
                              <div className="bg-gray-100 h-16 overflow-hidden">
                                <img src={fileUrl} alt={fileName} className="h-full w-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                            <a href={fileUrl} target="_blank" rel="noreferrer"
                              className="flex items-center justify-between p-2 text-xs text-[#f06600] hover:underline font-medium">
                              <span className="truncate pr-1">{fileName}</span>
                              <Download className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ManageTasks = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialFilter = searchParams.get('filter') || 'All';

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('assignments');
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDailyAssignmentModal, setShowDailyAssignmentModal] = useState(false);
  const [selectedUserIdForPerformance, setSelectedUserIdForPerformance] = useState(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', deadline: '', startDate: '', priority: 'Medium',
    assignedTo: '', project: '', adminNotes: '', referenceUrl: '', status: 'Pending'
  });
  const [files, setFiles] = useState([]);
  const [filterStatus, setFilterStatus] = useState(initialFilter);
  const [editId, setEditId] = useState(null);
  const [viewTask, setViewTask] = useState(null);
  const [updatesGroupMode, setUpdatesGroupMode] = useState(true); // grouped vs flat

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const headers = { Authorization: `Bearer ${token}` };
      const [taskRes, userRes, projectRes] = await Promise.all([
        axios.get(window.API_URL + '/api/portal/admin/tasks', { headers }),
        axios.get(window.API_URL + '/api/portal/admin/users', { headers }),
        axios.get(window.API_URL + '/api/portal/admin/projects', { headers })
      ]);
      setTasks(taskRes.data);
      setUsers(userRes.data);
      setProjects(projectRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUpdates = async () => {
    setLoadingUpdates(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const res = await axios.get(window.API_URL + '/api/portal/admin/tasks/updates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpdates(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUpdates(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchUpdates();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        // Avoid sending empty strings for optional references and dates
        if (key === 'project' || key === 'startDate') {
          if (formData[key]) submitData.append(key, formData[key]);
        } else {
          submitData.append(key, formData[key] ?? '');
        }
      });
      Array.from(files).forEach(file => submitData.append('files', file));

      if (editId) {
        await axios.put(`${window.API_URL}/api/portal/admin/tasks/${editId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({ icon: 'success', title: 'Task Updated', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      } else {
        await axios.post(window.API_URL + '/api/portal/admin/tasks', submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({ icon: 'success', title: 'Task Assigned', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || (editId ? 'Failed to update task' : 'Failed to assign task');
      Swal.fire('Error', errorMsg, 'error');
    }
  };

  const handleReview = async (id, status) => {
    let title = 'Review Task';
    let inputPlaceholder = 'Feedback or comments for the intern/employee...';
    let confirmBtn = 'Submit';
    let color = '#f06600';
    if (status === 'Completed') {
      title = 'Approve & Mark Task Completed';
      confirmBtn = 'Approve';
      color = '#16a34a';
    } else if (status === 'Changes Requested') {
      title = 'Request Changes / Revision';
      inputPlaceholder = 'Specify the changes or corrections needed before approval...';
      confirmBtn = 'Request Changes';
      color = '#ea580c';
    } else if (status === 'Rejected') {
      title = 'Reject Task';
      confirmBtn = 'Reject';
      color = '#dc2626';
    }

    const { value: fb, isConfirmed } = await Swal.fire({
      title,
      input: 'textarea',
      inputLabel: status === 'Changes Requested' ? 'Revision Instructions (Required)' : 'Feedback (Optional)',
      inputPlaceholder,
      inputValidator: (val) => {
        if (status === 'Changes Requested' && (!val || !val.trim())) {
          return 'Please specify what needs to be changed!';
        }
      },
      showCancelButton: true,
      confirmButtonColor: color,
      confirmButtonText: confirmBtn,
    });

    if (!isConfirmed) return;

    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      await axios.put(`${window.API_URL}/api/portal/admin/tasks/${id}/review`, { status, feedback: fb }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      Swal.fire({ icon: 'success', title: `Task marked as ${status}`, toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      setViewTask(null);
      fetchData();
    } catch (err) {
      Swal.fire('Error', 'Failed to review task', 'error');
    }
  };

  const handleEdit = (task) => {
    setEditId(task._id);
    setFormData({
      title: task.title,
      description: task.description,
      deadline: task.deadline ? task.deadline.split('T')[0] : '',
      startDate: task.startDate ? task.startDate.split('T')[0] : '',
      priority: task.priority,
      assignedTo: task.assignedTo?._id || '',
      project: task.project?._id || '',
      adminNotes: task.adminNotes || '',
      referenceUrl: task.referenceUrl || '',
      status: task.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Task?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        await axios.delete(`${window.API_URL}/api/portal/admin/tasks/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({ icon: 'success', title: 'Task Deleted', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
        setViewTask(null);
        fetchData();
      } catch (err) {
        Swal.fire('Error', 'Failed to delete task', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', deadline: '', startDate: '', priority: 'Medium', assignedTo: '', project: '', adminNotes: '', referenceUrl: '', status: 'Pending' });
    setFiles([]);
    setEditId(null);
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus === 'All') return true;
    if (filterStatus === 'Pending') return task.status === 'Pending';
    if (filterStatus === 'Active') return task.status === 'In Progress' || task.status === 'Submitted' || task.status === 'Under Review';
    if (filterStatus === 'Submitted') return task.status === 'Submitted';
    if (filterStatus === 'Under Review') return task.status === 'Under Review';
    if (filterStatus === 'Changes Requested') return task.status === 'Changes Requested';
    if (filterStatus === 'Completed') return task.status === 'Completed';
    if (filterStatus === 'Rejected') return task.status === 'Rejected';
    if (filterStatus === 'Overdue') {
      const daysLeft = getDaysLeft(task.deadline);
      return (daysLeft !== null && daysLeft < 0 && task.status !== 'Completed') || task.status === 'Overdue';
    }
    return task.status === filterStatus;
  });

  const getFileUrl = (filename) => {
    if (!filename) return '#';
    const bare = filename.startsWith('/uploads/') ? filename.replace('/uploads/', '') : filename.replace(/^\/+/, '');
    return `${window.API_URL}/uploads/${bare}`;
  };

  const getFileName = (filepath) => {
    if (!filepath) return 'File';
    return filepath.split('/').pop();
  };

  const getFileType = (filename) => {
    const ext = getFileName(filename).split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['mp4', 'webm', 'ogg'].includes(ext)) return 'video';
    return 'file';
  };

  const groupedUpdates = groupUpdatesByTask(updates);

  return (
    <div className="space-y-6 text-gray-900">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight">Manage Tasks & Progress</h2>
          <p className="text-gray-500 mt-1 text-sm">Assign multi-day tasks and track daily updates from your team.</p>
        </div>

        {activeTab === 'assignments' && (
          <div className="flex flex-wrap gap-2.5 items-center">
            <select
              className="bg-white border border-gray-200 rounded-xl px-3.5 py-2 text-sm focus:ring-2 focus:ring-[#f06600] outline-none text-gray-700 font-medium shadow-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Tasks ({tasks.length})</option>
              <option value="Pending">Pending</option>
              <option value="Active">Active / In Progress</option>
              <option value="Submitted">Submitted (Needs Review)</option>
              <option value="Under Review">Under Review</option>
              <option value="Changes Requested">Changes Requested</option>
              <option value="Completed">Completed</option>
              <option value="Rejected">Rejected</option>
              <option value="Overdue">Overdue</option>
            </select>

            <button
              onClick={() => setShowDailyAssignmentModal(true)}
              className="bg-gradient-to-r from-[#f06600] to-amber-600 hover:from-[#d05800] hover:to-amber-700 text-white px-4 py-2 rounded-xl transition-all font-semibold shadow-sm flex items-center gap-2 text-sm"
            >
              <CalendarPlus className="w-4 h-4" />
              + Daily Assignment
            </button>

            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 px-4 py-2 rounded-xl transition-colors font-semibold text-sm shadow-sm"
            >
              + Single Task
            </button>
          </div>
        )}

        {activeTab === 'updates' && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setUpdatesGroupMode(prev => !prev)}
              className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl transition-colors font-semibold text-sm shadow-sm"
            >
              <LayoutList className="w-4 h-4" />
              {updatesGroupMode ? 'Flat List' : 'Group by Task'}
            </button>
            <button
              onClick={fetchUpdates}
              className="border border-gray-200 bg-white hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-xl transition-colors font-semibold text-sm shadow-sm"
            >
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex bg-white border border-gray-200 p-1 rounded-2xl shadow-sm">
        {[
          { key: 'assignments', label: '📋 Task Assignments' },
          { key: 'updates', label: `📈 Daily Progress Updates${updates.length > 0 ? ` (${updates.length})` : ''}` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 px-6 font-bold text-sm rounded-xl transition-all ${
              activeTab === tab.key ? 'bg-[#f06600] text-white shadow-sm' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== TASK ASSIGNMENTS TAB ===== */}
      {activeTab === 'assignments' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredTasks.map(task => {
            const daysRunning = getDaysRunning(task);
            const daysLeft = getDaysLeft(task.deadline);
            const isOverdue = daysLeft !== null && daysLeft < 0 && task.status !== 'Completed';

            return (
              <div key={task._id} className="bg-white shadow-sm rounded-2xl p-5 border border-gray-200 hover:border-[#f06600]/50 hover:shadow-md transition-all flex flex-col">
                {/* Title & Priority */}
                <div className="flex justify-between items-start mb-2 gap-2">
                  <div className="flex items-center gap-2 flex-wrap flex-1">
                    {task.internshipDay && (
                      <span className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {getRoleDayPrefix(task.assignedTo)} Day {task.internshipDay}
                      </span>
                    )}
                    <h3 className="font-bold text-base text-gray-900 pr-1 leading-tight line-clamp-2">{task.title}</h3>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${PRIORITY_COLORS[task.priority] || 'bg-gray-100 text-gray-600'}`}>
                    {task.priority}
                  </span>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`self-start px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[task.status] || 'bg-gray-100 text-gray-600'}`}>
                    {task.status}
                  </span>
                  {task.category && (
                    <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md font-medium">
                      {task.category}
                    </span>
                  )}
                </div>

                <p className="text-gray-500 text-sm mb-4 flex-1 line-clamp-2">{task.description}</p>

                {/* Meta */}
                <div className="text-xs text-gray-500 space-y-1.5 mb-4">
                  <div className="flex items-center justify-between">
                    <p className="flex items-center gap-1">👤 <span className="text-gray-700 font-medium">{task.assignedTo?.fullName || 'Unknown'}</span></p>
                    {task.assignedTo?._id && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedUserIdForPerformance(task.assignedTo._id);
                          setShowPerformanceModal(true);
                        }}
                        className="text-[11px] font-bold text-[#f06600] hover:text-[#d05800] flex items-center gap-1 hover:underline"
                        title="View Intern Performance & Daily History"
                      >
                        <Award className="w-3 h-3" /> Profile
                      </button>
                    )}
                  </div>
                  {task.project?.title && <p className="flex items-center gap-1">📁 <span className="text-gray-700">{task.project.title}</span></p>}

                  {/* Deadline */}
                  <p className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-semibold' : ''}`}>
                    <Clock className="w-3.5 h-3.5" />
                    Deadline: {new Date(task.deadline).toLocaleDateString()}
                    {isOverdue && ' ⚠ Overdue'}
                    {!isOverdue && daysLeft !== null && daysLeft >= 0 && daysLeft <= 3 && (
                      <span className="text-orange-500 font-bold ml-1">({daysLeft}d left)</span>
                    )}
                  </p>

                  {/* Days running */}
                  {daysRunning !== null && daysRunning > 0 && task.status === 'In Progress' && (
                    <p className="flex items-center gap-1 text-[#f06600] font-semibold">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Running {daysRunning} day{daysRunning !== 1 ? 's' : ''}
                    </p>
                  )}

                  {task.attachments?.length > 0 && (
                    <p className="flex items-center gap-1 text-[#f06600]">
                      <Paperclip className="w-3 h-3" /> {task.attachments.length} attachment(s)
                    </p>
                  )}
                  {task.submittedFiles?.length > 0 && (
                    <p className="flex items-center gap-1 text-purple-600 font-medium">
                      <FileText className="w-3 h-3" /> {task.submittedFiles.length} submission file(s)
                    </p>
                  )}
                </div>

                {/* Submission / Changes feedback badge */}
                {(task.status === 'Submitted' || task.status === 'Under Review') && (
                  <div className="mb-3 p-2 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-700 font-semibold">
                    📬 Work submitted — ready for review
                  </div>
                )}
                {task.status === 'Changes Requested' && task.feedback && (
                  <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800">
                    <span className="font-bold">Revision Requested:</span> {task.feedback}
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100 flex-wrap gap-2">
                  <div className="flex gap-2">
                    <button onClick={() => setViewTask(task)}
                      className="text-[#f06600] hover:text-white hover:bg-[#f06600] border border-[#f06600]/40 p-1.5 rounded-lg transition-all" title="View Details">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleEdit(task)}
                      className="text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-200 p-1.5 rounded-lg transition-all" title="Edit Task">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(task._id)}
                      className="text-red-500 hover:text-white hover:bg-red-500 border border-red-200 p-1.5 rounded-lg transition-all" title="Delete Task">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {(task.status === 'Submitted' || task.status === 'Under Review') && (
                    <div className="flex gap-1.5 flex-wrap">
                      <button onClick={() => handleReview(task._id, 'Completed')}
                        className="bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors" title="Approve">
                        ✓ Approve
                      </button>
                      <button onClick={() => handleReview(task._id, 'Changes Requested')}
                        className="bg-orange-100 text-orange-800 hover:bg-orange-200 px-2 py-1 rounded-lg text-xs font-semibold transition-colors" title="Request Changes">
                        Changes
                      </button>
                      <button onClick={() => handleReview(task._id, 'Rejected')}
                        className="bg-red-100 text-red-700 hover:bg-red-200 px-2 py-1 rounded-lg text-xs font-semibold transition-colors" title="Reject">
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {filteredTasks.length === 0 && (
            <div className="col-span-full text-center text-gray-400 py-16 bg-white rounded-2xl border border-gray-200">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-lg font-semibold">No tasks found</p>
              <p className="text-sm mt-1">Try a different filter or assign a new task.</p>
            </div>
          )}
        </div>
      ) : (
        /* ===== DAILY UPDATES TAB ===== */
        <div>
          {loadingUpdates ? (
            <div className="text-center py-16 text-gray-500 font-medium">Loading updates...</div>
          ) : updates.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-2xl text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-lg font-semibold">No progress updates yet</p>
              <p className="text-sm mt-1">Updates will appear here when interns/employees log their daily progress.</p>
            </div>
          ) : updatesGroupMode ? (
            /* ─── GROUPED BY TASK ─── */
            <div className="space-y-4">
              <p className="text-xs text-gray-500 font-medium">
                Showing <strong className="text-gray-800">{groupedUpdates.length}</strong> task{groupedUpdates.length !== 1 ? 's' : ''} with updates • <strong className="text-gray-800">{updates.length}</strong> total update{updates.length !== 1 ? 's' : ''}
              </p>
              {groupedUpdates.map((group, i) => (
                <TaskUpdateGroup
                  key={group.task?._id || i}
                  group={group}
                  getFileUrl={getFileUrl}
                  getFileName={getFileName}
                  getFileType={getFileType}
                />
              ))}
            </div>
          ) : (
            /* ─── FLAT LIST (original) ─── */
            <div className="grid grid-cols-1 gap-5">
              {updates.map(up => (
                <div key={up._id} className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start flex-wrap gap-2 mb-4">
                    <div>
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                        {new Date(up.createdAt).toLocaleString()}
                      </span>
                      <h4 className="font-bold text-gray-900 text-lg">
                        {up.user?.fullName || 'Unknown User'}
                        <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full ml-2">{up.user?.role}</span>
                        {up.user?.fieldOrDepartment && <span className="text-xs text-gray-500 ml-2">({up.user.fieldOrDepartment})</span>}
                      </h4>
                      <p className="text-sm text-[#f06600] font-semibold mt-1">Task: {up.task?.title || 'Deleted Task'}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
                      up.updateType === 'Final' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>
                      {up.updateType === 'Final' ? '🏁 Final Work Submission' : '📋 Daily Progress Update'}
                    </span>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Update Notes</p>
                    <p className="text-gray-800 text-sm whitespace-pre-wrap font-medium">{up.updateNotes}</p>
                  </div>
                  {(up.githubLink || up.liveLink) && (
                    <div className="flex flex-wrap gap-3 mb-4">
                      {up.githubLink && (
                        <a href={up.githubLink} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-gray-950 hover:bg-gray-800 text-white rounded-lg text-xs font-semibold transition-colors">
                          <FaGithub className="w-3.5 h-3.5" /> View Repo
                        </a>
                      )}
                      {up.liveLink && (
                        <a href={up.liveLink} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-lg text-xs font-semibold transition-colors">
                          <ExternalLink className="w-3.5 h-3.5" /> Live Demo
                        </a>
                      )}
                    </div>
                  )}
                  {up.submittedFiles?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Attached Files ({up.submittedFiles.length})</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {up.submittedFiles.map((file, idx) => {
                          const fileUrl = getFileUrl(file);
                          const fileName = getFileName(file);
                          const isImg = getFileType(file) === 'image';
                          return (
                            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                              {isImg && <div className="bg-gray-100 h-20 overflow-hidden"><img src={fileUrl} alt={fileName} className="h-full w-full object-cover" onError={e => { e.target.style.display = 'none'; }} /></div>}
                              <a href={fileUrl} target="_blank" rel="noreferrer"
                                className="flex items-center justify-between p-2 text-xs text-[#f06600] hover:underline font-bold">
                                <span className="truncate">{fileName}</span>
                                <Download className="w-3.5 h-3.5 text-gray-400" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== VIEW TASK MODAL ===== */}
      {viewTask && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="flex justify-between items-start p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{viewTask.title}</h3>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[viewTask.status]}`}>{viewTask.status}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_COLORS[viewTask.priority]}`}>{viewTask.priority} Priority</span>
                  {(() => {
                    const d = getDaysRunning(viewTask);
                    return d !== null && d > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-[#f06600] flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {d} day{d !== 1 ? 's' : ''} running
                      </span>
                    ) : null;
                  })()}
                </div>
              </div>
              <button onClick={() => setViewTask(null)} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Task Info */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Task Details</h4>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <p className="text-gray-700">{viewTask.description}</p>
                  <div className="grid grid-cols-2 gap-3 mt-3 text-xs text-gray-500">
                    <p>👤 Assigned to: <span className="text-gray-800 font-medium">{viewTask.assignedTo?.fullName || 'Unknown'}</span></p>
                    <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> Deadline: <span className="text-gray-800 font-medium">{new Date(viewTask.deadline).toLocaleDateString()}</span></p>
                    {viewTask.startDate && <p className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Start: <span className="text-gray-800 font-medium">{new Date(viewTask.startDate).toLocaleDateString()}</span></p>}
                    {viewTask.project?.title && <p>📁 Project: <span className="text-gray-800 font-medium">{viewTask.project.title}</span></p>}
                  </div>
                  {viewTask.adminNotes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Admin Notes:</p>
                      <p className="text-gray-700 text-sm">{viewTask.adminNotes}</p>
                    </div>
                  )}
                  {viewTask.referenceUrl && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 mb-1.5">Reference Links &amp; Resources:</p>
                      <div className="flex flex-wrap gap-2">
                        {viewTask.referenceUrl.split(/[\s,\n\r]+/).filter(Boolean).map((url, uIdx) => {
                          const fullUrl = url.startsWith('http') ? url : `https://${url}`;
                          const isFigma = fullUrl.toLowerCase().includes('figma.com');
                          return (
                            <a
                              key={uIdx}
                              href={fullUrl}
                              target="_blank"
                              rel="noreferrer"
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                                isFigma
                                  ? 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200'
                                  : 'bg-orange-50 hover:bg-orange-100 text-[#f06600] border-orange-200'
                              }`}
                            >
                              {isFigma ? (
                                <SiFigma className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                              ) : (
                                <ExternalLink className="w-3.5 h-3.5 text-[#f06600] shrink-0" />
                              )}
                              <span className="truncate max-w-xs">{fullUrl.replace(/^https?:\/\//, '')}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Attachments */}
              {viewTask.attachments?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Admin Attachments</h4>
                  <div className="space-y-2">
                    {viewTask.attachments.map((file, i) => (
                      <a key={i} href={getFileUrl(file)} target="_blank" rel="noreferrer"
                        className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-100 rounded-lg hover:bg-orange-100 transition-colors group">
                        <Paperclip className="w-4 h-4 text-[#f06600] shrink-0" />
                        <span className="text-sm text-gray-700 flex-1 truncate">{getFileName(file)}</span>
                        <Download className="w-4 h-4 text-gray-400 group-hover:text-[#f06600] transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Submission Section */}
              {(viewTask.submittedFiles?.length > 0 || viewTask.githubLink || viewTask.liveLink || viewTask.submissionNotes || viewTask.reasonForIncomplete) ? (
                <div>
                  <h4 className="text-xs font-semibold text-purple-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Submission by {viewTask.assignedTo?.fullName || 'User'}
                  </h4>
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 space-y-4">
                    <div className="flex flex-wrap gap-3">
                      {viewTask.githubLink && (
                        <a href={viewTask.githubLink} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors font-medium">
                          <FaGithub className="w-4 h-4" /> GitHub Repository
                        </a>
                      )}
                      {viewTask.liveLink && (
                        <a href={viewTask.liveLink} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors font-medium">
                          <ExternalLink className="w-4 h-4" /> Live Demo
                        </a>
                      )}
                    </div>
                    {viewTask.submissionNotes && (
                      <div>
                        <p className="text-xs font-semibold text-purple-600 mb-1">Submission Notes:</p>
                        <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-purple-100">{viewTask.submissionNotes}</p>
                      </div>
                    )}
                    {viewTask.reasonForIncomplete && (
                      <div>
                        <p className="text-xs font-semibold text-red-500 mb-1">Reason for Incomplete:</p>
                        <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-red-100">{viewTask.reasonForIncomplete}</p>
                      </div>
                    )}
                    {viewTask.submittedFiles?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-purple-600 mb-2">Submitted Files ({viewTask.submittedFiles.length}):</p>
                        <div className="space-y-2">
                          {viewTask.submittedFiles.map((file, i) => {
                            const fileType = getFileType(file);
                            const fileUrl = getFileUrl(file);
                            return (
                              <div key={i} className="rounded-lg overflow-hidden border border-purple-100">
                                {fileType === 'image' && (
                                  <div className="bg-gray-100 p-2">
                                    <img src={fileUrl} alt={getFileName(file)} className="max-h-40 w-full object-contain rounded" onError={e => { e.target.style.display = 'none'; }} />
                                  </div>
                                )}
                                <a href={fileUrl} target="_blank" rel="noreferrer"
                                  className="flex items-center gap-3 p-3 bg-white hover:bg-purple-50 transition-colors">
                                  <FileText className="w-4 h-4 text-purple-500 shrink-0" />
                                  <span className="text-sm text-gray-700 flex-1 truncate">{getFileName(file)}</span>
                                  <Download className="w-4 h-4 text-gray-400" />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No final submission yet</p>
                  <p className="text-xs mt-1">The intern/employee may have posted daily progress updates.</p>
                </div>
              )}

              {/* Admin Feedback */}
              {viewTask.feedback && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Admin Feedback</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700">{viewTask.feedback}</div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100">
                <button onClick={() => { setViewTask(null); handleEdit(viewTask); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-xl text-sm font-semibold transition-colors">
                  <Edit className="w-4 h-4" /> Edit Task
                </button>
                <button onClick={() => handleDelete(viewTask._id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-xl text-sm font-semibold transition-colors">
                  <Trash2 className="w-4 h-4" /> Delete Task
                </button>
                {(viewTask.status === 'Submitted' || viewTask.status === 'Under Review' || viewTask.status === 'Changes Requested') && (
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <button onClick={() => handleReview(viewTask._id, 'Completed')}
                      className="flex items-center gap-1.5 px-4 py-2 bg-green-500 text-white hover:bg-green-600 rounded-xl text-sm font-semibold transition-colors">
                      ✓ Approve
                    </button>
                    <button onClick={() => handleReview(viewTask._id, 'Changes Requested')}
                      className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white hover:bg-orange-600 rounded-xl text-sm font-semibold transition-colors">
                      Request Changes
                    </button>
                    <button onClick={() => handleReview(viewTask._id, 'Rejected')}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white hover:bg-red-600 rounded-xl text-sm font-semibold transition-colors">
                      ✕ Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ASSIGN / EDIT TASK MODAL ===== */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white shadow-xl rounded-2xl p-6 w-full max-w-md border border-gray-200 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold text-gray-900">{editId ? 'Edit Task' : 'Assign New Task'}</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder="Task Title" required
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })} />

              <textarea placeholder="Description" required
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none h-24 resize-none text-sm"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })} />

              {/* Dates row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 block flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" /> Start Date
                  </label>
                  <input type="date"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-700 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                    value={formData.startDate}
                    onChange={e => setFormData({ ...formData, startDate: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 block flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-red-400" /> Deadline *
                  </label>
                  <input type="date" required
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-700 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                    value={formData.deadline}
                    onChange={e => setFormData({ ...formData, deadline: e.target.value })} />
                </div>
              </div>

              {formData.startDate && formData.deadline && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-2 text-xs text-blue-700 font-medium flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                  Task duration: <strong>
                    {Math.max(0, Math.ceil((new Date(formData.deadline) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24)))} day(s)
                  </strong>
                </div>
              )}

              {/* Priority */}
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Priority</label>
                <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                  value={formData.priority}
                  onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              {/* Assign To */}
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Assign To *</label>
                <select required
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                  value={formData.assignedTo}
                  onChange={e => setFormData({ ...formData, assignedTo: e.target.value })}>
                  <option value="">Select Employee / Intern</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.fullName} ({u.role})</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Link to Project</label>
                  <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                    value={formData.project}
                    onChange={e => setFormData({ ...formData, project: e.target.value })}>
                    <option value="">None</option>
                    {projects.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Status</label>
                  <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}>
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Completed">Completed</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Admin Notes (Optional)</label>
                <textarea placeholder="Notes for the assignee..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none h-16 resize-none text-sm"
                  value={formData.adminNotes}
                  onChange={e => setFormData({ ...formData, adminNotes: e.target.value })} />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Reference Links / Resources (Figma, GitHub, Docs, etc.)</label>
                <input
                  type="text"
                  placeholder="https://www.figma.com/file/... or requirement links (separate multiple with commas or spaces)"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none text-sm"
                  value={formData.referenceUrl}
                  onChange={e => setFormData({ ...formData, referenceUrl: e.target.value })}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Attachments (Optional)</label>
                <input type="file" multiple
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#f06600]/20 file:text-[#f06600] hover:file:bg-[#f06600]/30"
                  onChange={e => setFiles(e.target.files)} />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 px-4 py-2.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl transition-colors font-semibold text-sm">
                  {editId ? 'Save Changes' : 'Assign Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Daily Assignment Modal */}
      <CreateDailyAssignmentModal
        isOpen={showDailyAssignmentModal}
        onClose={() => {
          setShowDailyAssignmentModal(false);
          fetchData();
        }}
        onCreated={() => {
          fetchData();
        }}
      />

      {/* User Performance & History Modal */}
      <UserPerformanceModal
        isOpen={showPerformanceModal}
        onClose={() => {
          setShowPerformanceModal(false);
          setSelectedUserIdForPerformance(null);
          fetchData();
        }}
        userId={selectedUserIdForPerformance}
      />
    </div>
  );
};

export default ManageTasks;
